import {
  alerter,
} from '@codev/helix-react';
import React from 'react';
import PropTypes from 'prop-types';
import Frame from '../Frame.jsx';
import FormPanel from '../FormPanel.jsx';
import languagesPropType from '../languagesPropType.js';
import window from '../globalWrappers/window.js';
import handleNetworkError from '../utilities/handleNetworkError.js';
import Retrier from '../utilities/Retrier.js';
import createLoginLink from '../forgotPassword/createLoginLink.js';
import Api from './Api.js';
import EmailAddressForm from './EmailAddressForm.jsx';
import NoRecovery from './NoRecovery.jsx';
import PhoneConfirmationForm from './PhoneConfirmationForm.jsx';
import ResetPasswordForm from './ResetPasswordForm.jsx';
import SuccessPanel from './SuccessPanel.jsx';
import VerifyCodeForm from './VerifyCodeForm.jsx';
import translate from './translate.js';

class ForgotPasswordView extends React.Component {
  constructor(props) {
    super(props);

    this.retrier = new Retrier({
      mode: "alerter",
    });

    this.initializeStates();

    this.api = Api({
      token: props.antiforgeryConfig.value,
    });

    this.state = {
      authorizationToken: null,
      email: '',
      schemeUid: null,
      lastTwoDigits: '',
      password: '',
      phoneNumber: '',
      recoveryState: props.webmailSiteAllowsRecovery ? 'emailAddress' : 'noRecovery',
      schemeType: null,
      submitting: false,
      verificationCode: '',
    };
  }

  componentDidUpdate = (prevProps) => {
    if (prevProps.language != this.props.language) {
      this.initializeStates();
      this.setState({});
    }
  }

  backToLogin = () =>  window.location = createLoginLink();

  initializeStates = () => {
    let {
      language,
    } = this.props;
    let {
      backToLogin,
      handleRecaptcha,
      handleResendCode,
      handleSubmitAddress,
      handleSubmitAddressAndPhoneNumber,
      handleSubmitChallengeResponse,
      handleSubmitResetPassword,
    } = this;

    this.recoveryStates = {
      emailAddress: {
        onSubmit: handleRecaptcha,
        props: {
          cancelText: translate('backToLoginPage', language),
          onCancel: backToLogin,
          showCancelButton: true,
          submitText: translate('continue', language),
        },
        content: () => (
          <EmailAddressForm
            captchaSiteKey={this.props.captchaSiteKey}
            email={this.state.email}
            language={this.props.language}
            onChange={e => this.setState({email: e.target.value})}
            onSubmit={handleSubmitAddress}
          />
        ),
      },
      phoneConfirmation: {
        onSubmit: handleSubmitAddressAndPhoneNumber,
        props: {
          cancelText: translate('backToLoginPage', language),
          onCancel: backToLogin,
          showCancelButton: true,
          submitText: translate('continue', language),
        },
        content: () => (
          <PhoneConfirmationForm
            language={language}
            lastTwoDigits={this.state.lastTwoDigits}
            onChange={phoneNumber => this.setState({phoneNumber: phoneNumber || ''})}
            phoneNumber={this.state.phoneNumber}
          />
        )
      },
      verifyCode: {
        onSubmit: handleSubmitChallengeResponse,
        props: {
          cancelText: translate('backToLoginPage', language),
          onCancel: backToLogin,
          showCancelButton: true,
          submitText: translate('continue', language),
        },
        content: () => (
          <VerifyCodeForm
            language={language}
            onChange={x => this.setState({ verificationCode: x.target.value })}
            onResendCode={handleResendCode}
            phoneNumber={this.state.phoneNumber}
            schemeType={this.state.schemeType}
            verificationCode={this.state.verificationCode}
          />
        )
      },
      resetPassword: {
        onSubmit: handleSubmitResetPassword,
        props: {
          cancelText: translate('backToLoginPage', language),
          onCancel: backToLogin,
          showCancelButton: true,
          submitText: translate('resetPassword', language),
        },
        content: () => (
          <ResetPasswordForm
            language={language}
            onChange={password => this.setState({password})}
            value={this.state.password}
          />
        )
      },
      success: {
        onSubmit: backToLogin,
        props: {
          submitText: translate('backToLoginPage', language),
        },
        content: () => (
          <SuccessPanel
            language={language}
          />
        )
      },
      noRecovery: {
        onSubmit: backToLogin,
        props: {
          submitText: translate('backToLoginPage', language),
        },
        content: () => (
          <NoRecovery
            language={language}
          />
        )
      }
    };
  }

  handleRecaptcha = (e) => {
    e.resetFormSubmit();
    window.grecaptcha.execute();
  }

  handleSubmit = async (event, {
    action,
    retryMessage,
  }) => {
    event?.resetFormSubmit();
    await this.retrier.try({
      retryMessage,
      action: async () => {
        this.setState({
          submitting: true,
        });

        alerter.dismiss();

        await handleNetworkError(async () => {
          await action().finally(() => this.setState({submitting: false}));
        });
      },
    });
  }

  handleSubmitAddress = async (captcha) => {
    await this.handleSubmit(null, {
      action: async () => {
        let {
          email,
        } = this.state;

        let response = await this.api.checkEmail(email, captcha);

        if (response) {
          if (response.schemeType === 'TOTP') {
            this.setState({
              schemeType: response.schemeType,
              schemeUid: response.schemeUid,
              recoveryState: 'verifyCode',
            });
          } else {
            this.setState({
              schemeType: response.schemeType,
              lastTwoDigits: response.lastTwoDigits,
              schemeUid: response.schemeUid,
              recoveryState: 'phoneConfirmation',
            });
          }
        } else {
          this.setState({
            recoveryState: 'noRecovery',
          });
        }
      },
      retryMessage: translate('errorSubmitAddress', this.props.language),
    });
  }

  handleResendCode = async () => {
    await this.retrier.try({
      action: async () => await this.api.confirmEmailAndPhoneNumber(this.state.email, this.state.phoneNumber),
      retryMessage: translate('errorResendCode', this.props.language),
    });
  }

  handleSubmitAddressAndPhoneNumber = async (e) => {
    await this.handleSubmit(e, {
      action: async () => {
        let {
          email,
          phoneNumber,
        } = this.state;

        let response = await this.api.confirmEmailAndPhoneNumber(email, phoneNumber);

        this.setState({
          schemeUid: response.schemeUid,
          recoveryState: 'verifyCode',
        });
      },
      retryMessage: translate('errorSubmitAddressAndPhoneNumber', this.props.language),
    });
  }

  handleSubmitResetPassword = async (e) => {
    await this.handleSubmit(e, {
      action: async () => {
        let {
          authorizationToken,
          password,
        } = this.state;

        await this.api.resetPassword({
          authorizationToken,
          password,
        });

        this.setState({
          recoveryState:'success',
        });
      },
      retryMessage: translate('errorSubmitResetPassword', this.props.language),
    });
  }

  handleSubmitChallengeResponse = async (e) => {
    await this.handleSubmit(e, {
      action: async () => {
        let {
          schemeUid,
          email,
          verificationCode,
        } = this.state;

        let response = await this.api.verifyChallenge({
            emailAddress: email,
            schemeUid: schemeUid,
            verificationCode,
        });

        this.setState({
          authorizationToken: response.token,
          recoveryState:'resetPassword',
        });
      },
      retryMessage: translate('errorSubmitChallengeResponse', this.props.language),
    });
  }

  render() {
    let recoveryData = this.recoveryStates[this.state.recoveryState];

    return (
      <Frame
        title={translate('passwordRecovery', this.props.language)}
      >
        <FormPanel
          onSubmit={recoveryData.onSubmit}
          submitting={this.state.submitting}
          {...recoveryData.props}
        >
          {recoveryData.content()}
        </FormPanel>
      </Frame>
    );
  }
}

ForgotPasswordView.defaultProps = {
  language: languagesPropType.defaultProp,
};

ForgotPasswordView.propTypes = {
  antiforgeryConfig: PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.string,
  }),
  captchaSiteKey: PropTypes.string,
  language: languagesPropType.propType,
  webmailSiteAllowsRecovery: PropTypes.bool,
};

export default ForgotPasswordView;
